import type { ChangeEvent } from 'react';
import {
    Button,
    ButtonRow,
    Card,
    FormErrorMessage,
    FormGroup,
    FormSection,
    Radio,
    RadioGroup,
    TextArea,
} from '@ksyos/design-system';
import { useState } from 'react';
import type { IStepProps } from '../NewGhcRequest';
import { translateKey } from '../../../../translate';
import { LoaderSpinner } from '../../../elements/atoms/loaderSpinner/LoaderSpinner';
import { AssetDropzoneWithThumbnails } from '../../../elements/organisms/assetDropzoneWithThumbnails/AssetDropzoneWithThumbnails';

const durationOptions = ['1 dag', 'Tussen 2 en 5 dagen', 'Ongeveer een week', 'Langer dan 2 weken'];

export function Step2(props: IStepProps) {
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const continueButtonText = isUploading ? 'Je foto wordt geladen...' : 'Volgende';

    return (
        <>
            <Card.Header title=" Geef zo volledig mogelijk antwoord. Dan kunnen we je zo goed mogelijk helpen." />
            <Card.Body>
                <FormSection>
                    <FormGroup
                        hasErrors={props.errors.has('complaints')}
                        label="Wat zijn je klachten?"
                    >
                        <TextArea
                            error={
                                props.errors.get('complaints')
                                    ? translateKey('validation.' + props.errors.get('complaints'))
                                    : undefined
                            }
                            onChange={(e) => props.handleChange({ complaints: e.target.value || '' })}
                            placeholder="Beschrijf je klachten..."
                            value={props.request.customControlData.complaints || ''}
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={props.errors.has('complaintsSeverity')}
                        label="Hoeveel last heb je van de klachten?"
                    >
                        <TextArea
                            error={
                                props.errors.get('complaintsSeverity')
                                    ? translateKey('validation.' + props.errors.get('complaintsSeverity'))
                                    : undefined
                            }
                            onChange={(e) => {
                                props.handleChange({ complaintsSeverity: e.target.value || '' });
                            }}
                            placeholder="Beschrijf je hoeveel last je hebt..."
                            value={props.request.customControlData.complaintsSeverity || ''}
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={props.errors.has('complaintsLocation')}
                        label="Waar voel je de klachten precies?"
                    >
                        <TextArea
                            error={
                                props.errors.get('complaintsLocation')
                                    ? translateKey('validation.' + props.errors.get('complaintsLocation'))
                                    : undefined
                            }
                            onChange={(e) => {
                                props.handleChange({ complaintsLocation: e.target.value || '' });
                            }}
                            placeholder="Beschrijf waar je de klachten voelt..."
                            value={props.request.customControlData.complaintsLocation || ''}
                        />
                    </FormGroup>

                    <FormGroup label=" Heeft je huisarts gevraagd om een foto? Of helpt een foto om de klacht te omschrijven?">
                        <AssetDropzoneWithThumbnails
                            acceptedFiles=".png,.jpeg,.jpg,.mp4,.mov,.heic,.heif"
                            files={props.request.customControlData.files}
                            handleChange={(files) => props.handleChange({ files })}
                            id={0}
                            limit={4}
                            onUploadStatusChanged={(uploadingStatus: boolean) => {
                                setIsUploading(uploadingStatus);

                                if (props.onUploadStatusChanged) {
                                    props.onUploadStatusChanged(uploadingStatus);
                                }
                            }}
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={props.errors.has('complaintsDuration')}
                        label="Hoelang heb je last van de klachten?"
                    >
                        <RadioGroup
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                props.handleChange({ complaintsDuration: event.target.value });
                            }}
                            value={props.request.customControlData.complaintsDuration}
                        >
                            {durationOptions.map((option) => (
                                <Radio
                                    id={option}
                                    key={option}
                                    label={option}
                                    value={option}
                                />
                            ))}
                        </RadioGroup>
                    </FormGroup>

                    <FormGroup label="">
                        {props.errors.has('complaintsDuration') && (
                            <FormErrorMessage>
                                {translateKey('validation.' + props.errors.get('complaintsDuration'))}
                            </FormErrorMessage>
                        )}
                    </FormGroup>
                </FormSection>
                <FormSection isBorderless>
                    <ButtonRow justify="center">
                        <Button
                            isDisabled={isUploading}
                            onClick={props.validateAndGoToNextStep}
                            variant="solid"
                        >
                            {continueButtonText}
                            {isUploading && <LoaderSpinner />}
                        </Button>
                    </ButtonRow>
                </FormSection>
            </Card.Body>
        </>
    );
}
